import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, LogIn, CheckCircle } from "lucide-react";
import { connectJotform } from '@/services/jotformService';

// Jotform JS SDK types
declare global {
  interface Window {
    JF: {
      initialize: (options: {
        appName?: string;
        accessType?: 'full' | 'readOnly';
        enableCookieAuth?: boolean;
      }) => void;
      login: (
        onSuccess: () => void,
        onError: (error: string) => void,
        onClose?: () => void
      ) => void;
      getAPIKey: () => string | null;
      logout: () => void;
    };
  }
}

const JOTFORM_SDK_URL = 'https://js.jotform.com/JotForm.js';

interface ConnectJotformButtonProps {
  onConnected?: () => void;
}

function getErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

const ConnectJotformButton: React.FC<ConnectJotformButtonProps> = ({ onConnected }) => {
  const [sdkLoaded, setSdkLoaded] = useState(false);
  const [sdkError, setSdkError] = useState<string | null>(null);
  const [connectError, setConnectError] = useState<string | null>(null);
  const [connectSuccess, setConnectSuccess] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Resize Jotform SDK modal when it appears
  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (!(node instanceof HTMLElement)) continue;
          // The SDK creates a fixed overlay with an iframe pointing to jotform.com
          const iframe = node.tagName === 'IFRAME' ? node as HTMLIFrameElement : node.querySelector?.('iframe');
          if (iframe && iframe instanceof HTMLIFrameElement) {
            const src = iframe.getAttribute('src') || '';
            if (src.includes('jotform.com')) {
              iframe.style.minHeight = '80vh';
              iframe.style.height = '80vh';
              // Also resize the parent container if it has fixed dimensions
              const container = iframe.parentElement;
              if (container && container.style.height) {
                container.style.height = '80vh';
                container.style.maxHeight = '90vh';
              }
            }
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  // Load Jotform SDK
  useEffect(() => {
    if (window.JF) {
      setSdkLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.src = JOTFORM_SDK_URL;
    script.async = true;

    script.onload = () => {
      if (window.JF) {
        window.JF.initialize({
          appName: 'FAB.day - Form Backup',
          accessType: 'full',
          enableCookieAuth: false,
        });
        setSdkLoaded(true);
      }
    };

    script.onerror = () => {
      setSdkError('Failed to load the Jotform login flow. Refresh the page and try again.');
      setSdkLoaded(false);
    };

    document.body.appendChild(script);
  }, []);

  // OAuth-style login via Jotform SDK
  const handleOAuthConnect = useCallback(() => {
    if (!window.JF) {
      setConnectError('Jotform login is not available right now. Refresh the page and try again.');
      return;
    }

    setConnectError(null);
    setConnectSuccess(null);
    setActionLoading('oauth');

    window.JF.login(
      async () => {
        const apiKeyFromSdk = window.JF.getAPIKey();
        if (!apiKeyFromSdk) {
          setConnectError('Failed to complete the Jotform login flow. Please try again.');
          setActionLoading(null);
          return;
        }

        try {
          const result = await connectJotform(apiKeyFromSdk);
          setConnectSuccess(`Connected as ${result.user.username} (${result.user.accountType})`);
          onConnected?.();
        } catch (error: unknown) {
          setConnectError(getErrorMessage(error, 'Failed to store credentials'));
        } finally {
          setActionLoading(null);
        }
      },
      (error: string) => {
        setConnectError(error || 'Login was cancelled or failed');
        setActionLoading(null);
      },
      () => {
        if (actionLoading === 'oauth') {
          setActionLoading(null);
        }
      }
    );
  }, [actionLoading, onConnected]);

  return (
    <div className="space-y-4">
      {connectSuccess && (
        <Alert>
          <CheckCircle className="h-4 w-4" />
          <AlertDescription>{connectSuccess}</AlertDescription>
        </Alert>
      )}

      {sdkError && (
        <Alert>
          <AlertDescription>{sdkError}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-3">
        <Button
          onClick={handleOAuthConnect}
          disabled={actionLoading === 'oauth' || !sdkLoaded}
          className="w-full"
          size="lg"
        >
          {actionLoading === 'oauth' ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <LogIn className="h-4 w-4 mr-2" />
          )}
          {sdkLoaded ? 'Connect with Jotform' : 'Loading Jotform Login...'}
        </Button>
        <p className="text-sm text-muted-foreground text-center">
          Sign in to your Jotform account to authorize access.
        </p>
        <p className="text-xs text-muted-foreground text-center">
          Not logged into Jotform?{' '}
          <a
            href="https://www.jotform.com/login"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary underline"
          >
            Log in on jotform.com first
          </a>
          , then come back and click Connect.
        </p>
        {sdkError && (
          <p className="text-xs text-muted-foreground text-center">
            If the login button never becomes available, refresh this page and try again.
          </p>
        )}
      </div>

      {connectError && (
        <Alert variant="destructive">
          <AlertDescription>{connectError}</AlertDescription>
        </Alert>
      )}
    </div>
  );
};

export default ConnectJotformButton;
